import React from "react";
import "../images.css";
import * as opOrderStatsService from "../services/openprintStats.service";
import * as ipOrderStatsService from "../services/iprintStats.service";
import * as spOrderStatsService from "../services/subprintStats.service";
import { finishItemId } from "./helpers/finishItemId";

const SERVICES = {
  OP: opOrderStatsService,
  IP: ipOrderStatsService,
  SP: spOrderStatsService,
};

function resolveBarcodes(orderId, itemIds, orderItems, system) {
  if (orderItems && orderItems.length) {
    return orderItems.map((item) => finishItemId(item.order_item_id, system));
  }
  if (!itemIds || !itemIds.length) {
    return [];
  }
  const barcodes = [];
  for (let i = 0; i < itemIds.length; i++) {
    const row = itemIds[i];
    if (String(row[0]) === String(orderId) && row[1]) {
      barcodes.push(row[1]);
    }
  }
  return barcodes;
}

export default function OrderArtworkButton({
  orderId,
  itemIds,
  orderItems,
  preloadedArtworks,
  system = "OP",
  label = "View",
  size = "md",
}) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [artworks, setArtworks] = React.useState([]);

  const service = SERVICES[system];

  const loadArtwork = async (event) => {
    event.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }

    // Some systems (e.g. SubPrint) already have the artwork path stored on the order
    // item once it's composited — use it directly instead of the live barcode lookup,
    // which requires an extra round trip to an external service.
    if (preloadedArtworks) {
      setArtworks(preloadedArtworks);
      setOpen(true);
      return;
    }

    const barcodes = resolveBarcodes(orderId, itemIds, orderItems, system);
    if (!barcodes.length || !service) {
      return;
    }

    setLoading(true);
    const collected = [];
    for (const barcode of barcodes) {
      try {
        const result = await service.getArtwork(barcode);
        if (result && result.artworks) {
          collected.push(...result.artworks);
        }
      } catch (err) {
        console.log(err);
      }
    }
    setArtworks(collected);
    setLoading(false);
    setOpen(true);
  };

  const closeArtwork = (event) => {
    event.stopPropagation();
    setOpen(false);
  };

  const buttonClass =
    size === "sm"
      ? "bg-gray-500 text-white active:bg-gray-600 uppercase text-xs px-1 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
      : "bg-gray-500 text-white active:bg-gray-600 uppercase text-md px-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150";

  if (open) {
    return (
      <div onClick={closeArtwork}>
        <div className="justify-center items-center overflow-x-hidden overflow-y-auto fixed inset-x-0 inset-y-0 z-[100] max-h-screen w-full max-w-screen outline-none focus:outline-none">
          <div
            onClick={(e) => e.stopPropagation()}
            className="modal-dialog relative w-auto mx-auto max-w-5xl"
          >
            <div className="bg-slate-500 border-0 rounded-lg shadow-lg relative flex flex-col w-full outline-none focus:outline-none">
              <div className="flex bg-slate-500 items-start justify-between p-2 border-b border-solid border-slate-200 rounded-t">
                <h3 className="text-2xl text-white font-semibold">
                  Order Artworks: {orderId}
                </h3>
                <button
                  type="button"
                  className="mb-1 ml-auto bg-transparent border-0 text-white float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                  onClick={closeArtwork}
                >
                  <span className="text-white h-6 w-6 text-2xl block outline-none focus:outline-none">
                    x
                  </span>
                </button>
              </div>
              <div className="relative text-center overflow-x-auto overflow-y-auto shadow-md sm:rounded-lg pt-12 pl-12 pb-8 pr-4">
                {artworks.length ? (
                  artworks.map((obj, index) => (
                    <img
                      key={`${obj.barcode_id || index}-${index}`}
                      className="thumbnail zoom_2 mt-12"
                      src={obj.artwork_path}
                      alt={`Artwork ${index + 1}`}
                      style={{
                        width: "95%",
                        height: "95%",
                        border: ".5px solid #555",
                      }}
                    />
                  ))
                ) : (
                  <p className="text-white py-8">No artwork found for this order.</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="opacity-25 fixed inset-0 z-[90] bg-black" />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={loadArtwork}
      className={buttonClass}
      disabled={loading}
    >
      {loading ? "..." : label}
    </button>
  );
}
